import {default as viewportSizesDefault} from "./viewports";
import {Result, ViewportSize} from "./types";
export default class EmbedsSizesGenerator {

    protected controller: AbortController
    protected mainClass: string;
    private results: Array<Result>;
    readonly markup: string;
    readonly viewportSizes: Array<ViewportSize>
    readonly embedName: string|null;

    constructor(markup: string, viewportSizes?: Array<ViewportSize>, embedName?: string) {
        this.controller = new AbortController();
        this.markup = markup;
        this.viewportSizes = viewportSizes || viewportSizesDefault;
        this.embedName = embedName || null;
    }

    async run() {

        document.getElementById("calculation").hidden = true;

        return await this.calculate(this.markup);
    }

    async calculate(markup) {
        const calculationSection = document.getElementById("calculation");
        calculationSection.hidden = false;
        calculationSection.scrollIntoView();

        const currentViewportWidthSpan = document.getElementById(
            "current-viewport-width"
        );
        const currentViewportHeightSpan = document.getElementById(
            "current-viewport-height"
        );

        const maxWidth = calculationSection.offsetWidth;

        const progress = document.querySelector("progress");
        progress.max = this.viewportSizes.length;
        let i = 1;
        progress.value = 0;

        const results = [];
        const signal = this.controller.signal;

        for (const viewportSize of this.viewportSizes) {
            progress.value = i;
            currentViewportWidthSpan.textContent = viewportSize.width.toString();
            currentViewportHeightSpan.textContent = viewportSize.height.toString();

            const data: any = await this.calculateViewportSize({
                maxWidth,
                markup,
                signal,
                ...viewportSize,
            });

            results.push({
                ...data,
                viewportSize,
            });

            i++;
        }
        calculationSection.hidden = true;

        results.sort((a, b) => {
            return b.viewportSize.width - a.viewportSize.width;
        });

        return results;
    }

    async terminate() {
        const terminationSection = document.getElementById("termination");
        terminationSection.hidden = false;

        // Make sure the results are sorted by viewport width in descending order.
        this.results.sort((a, b) => {
            return b.viewportSize.width - a.viewportSize.width;
        });

        const markupViewportSizesOl = document.getElementById(
            "markup-viewport-sizes"
        );
        while (markupViewportSizesOl.firstChild) {
            markupViewportSizesOl.removeChild(markupViewportSizesOl.firstChild);
        }
        for (const result of this.results) {
            const li = document.createElement("li");
            li.textContent = JSON.stringify(result);
            markupViewportSizesOl.appendChild(li);
        }

        const mainClass = this.mainClass;
        const containerId = `layout-shift-termination-${Math.random()}`.replace(
            ".",
            "-"
        );

        let styleTag = `<style class='${mainClass}'>`;
        styleTag += `\n@supports (contain: inline-size) {`;
        styleTag += `\n#${containerId} { container-type: inline-size; }`;
        styleTag += `\n#${containerId} > .${mainClass} { min-height:${this.results[0].height}px; }`;
        for (const result of this.results.slice(1)) {
            styleTag += `\n@container ( max-width: ${result.viewportSize.width}px ) { #${containerId} > .layout-shift-terminated { min-height:${result.height}px; } }`;
        }
        styleTag += "\n}";
        styleTag += "\n</style>";

        const terminateLayoutShift = function (root) {
            setTimeout(function () {
                [].map.call(root.querySelectorAll(`".${mainClass}"`), (el) =>
                    el.remove()
                );
            }, 4000);
        };

        let scriptTag = `<script class="${mainClass}" type="application/javascript" async>`;
        scriptTag += `(${terminateLayoutShift.toString()})(document.getElementById(${JSON.stringify(
            containerId
        )}))`;
        scriptTag += `</script>`;


        const optimizedMarkup = `\n${styleTag}\n${scriptTag}\n`;

        console.log(optimizedMarkup);
        return optimizedMarkup;

    }

    async calculateViewportSize({maxWidth, width, height, markup, signal}) {

        const oldIframe = document.querySelector("#iframe-wrapper iframe") || document.querySelector("iframe");
        const iframeWrapper = oldIframe.parentNode;

        const iframe = document.createElement("iframe");
        iframe.src = "about:blank";
        iframe.width = width;
        iframe.height = height;
        if (maxWidth >= width) {
            iframe.style.transform = "";
            // @ts-ignore
            iframeWrapper.style.height = "";
        } else {
            iframe.style.transform = `scale(${maxWidth / width})`;
            // @ts-ignore
            iframeWrapper.style.height = `${height * (maxWidth / width)}px`;
        }
        iframeWrapper.replaceChild(iframe, oldIframe);

        const isInstagramEmbed = this.embedName === 'instagram';
        const isGleamEmbed = ['gleam', 'embed_gleam'].indexOf(this.embedName) !== -1
            || markup.toLowerCase().indexOf('gleam') !== -1;

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width">
              <style>
                /* Add border to prevent margin collapsing. */
                /*body > div { border: solid 1px transparent; }*/
              </style>
            </head>
            <body>
              <div>${markup}</div>
              <hr>
              <script type="application/javascript">
              // import { watchForEmbedLoaded } from "./measurement";
              function watchForEmbedLoaded(container, isInstagramEmbed, isGleamEmbed) {
                  const windowLoaded = new Promise((resolve) => {
                    window.addEventListener("load", resolve);
                  });
                
                  const startTime = new Date();
                
                  return new Promise((resolve, reject) => {
                    const resolveWithResolution = (resolution) => {
                      resolve({
                        duration: new Date().valueOf() - startTime.valueOf(),
                        width: resolution && resolution.width ? resolution.width : container.offsetWidth,
                        height: resolution && resolution.height ? resolution.height : container.offsetHeight,
                      });
                    };

                    const getInstagramResolution = () => {
                      const iframe = container.querySelector(
                        "iframe.instagram-media-rendered, iframe.instagram-media"
                      );
                      if (!iframe) {
                        return null;
                      }

                      const attributeHeight = parseInt(
                        iframe.getAttribute("height") || "",
                        10
                      );
                      const styleHeight = parseInt(iframe.style.height || "", 10);
                      const height = attributeHeight || iframe.offsetHeight || styleHeight;
                      if (!height) {
                        return null;
                      }

                      return {
                        width: iframe.offsetWidth || container.offsetWidth,
                        height,
                      };
                    };

                    const getGleamResolution = () => {
                      const iframe = container.querySelector(
                        'iframe[src*="gleam.io"], iframe[src*="gleamjs.io"], iframe'
                      );
                      if (!iframe) {
                        return null;
                      }

                      const attributeHeight = parseInt(
                        iframe.getAttribute("height") || "",
                        10
                      );
                      const styleHeight = parseInt(iframe.style.height || "", 10);
                      const height = Math.max(
                        attributeHeight || 0,
                        iframe.offsetHeight || 0,
                        styleHeight || 0,
                        container.offsetHeight || 0,
                        container.scrollHeight || 0
                      );
                      if (!height) {
                        return null;
                      }

                      return {
                        width: iframe.offsetWidth || container.offsetWidth,
                        height,
                      };
                    };

                    const getGenericIframeResolution = () => {
                      const iframe = container.querySelector("iframe");
                      if (!iframe) {
                        return null;
                      }

                      const attributeHeight = parseInt(
                        iframe.getAttribute("height") || "",
                        10
                      );
                      const styleHeight = parseInt(iframe.style.height || "", 10);
                      const height = Math.max(
                        attributeHeight || 0,
                        iframe.offsetHeight || 0,
                        styleHeight || 0,
                        container.offsetHeight || 0,
                        container.scrollHeight || 0
                      );
                      if (!height) {
                        return null;
                      }

                      return {
                        width: iframe.offsetWidth || container.offsetWidth,
                        height,
                      };
                    };

                    if (isInstagramEmbed) {
                      let latestResolution = null;
                      let resolveTimeoutId = 0;
                      const minimumWaitDuration = 12000;
                      const stableWaitDuration = 2500;
                      const maximumWaitDuration = 18000;

                      const processInstagramEmbed = () => {
                        if (container.querySelector("iframe.instagram-media")) {
                          return;
                        }

                        if (
                          window.instgrm &&
                          window.instgrm.Embeds &&
                          typeof window.instgrm.Embeds.process === "function"
                        ) {
                          window.instgrm.Embeds.process();
                        }
                      };

                      const scheduleResolution = () => {
                        processInstagramEmbed();

                        const iframe = container.querySelector(
                          "iframe.instagram-media-rendered, iframe.instagram-media"
                        );
                        if (iframe && !iframe.dataset.emakiHeightListener) {
                          iframe.dataset.emakiHeightListener = "true";
                          iframe.addEventListener("load", scheduleResolution);
                        }

                        const resolution = getInstagramResolution();
                        if (!resolution) {
                          return;
                        }

                        latestResolution = resolution;
                        clearTimeout(resolveTimeoutId);
                        const elapsed = new Date().valueOf() - startTime.valueOf();
                        const waitForMinimumDuration = Math.max(
                          minimumWaitDuration - elapsed,
                          0
                        );
                        resolveTimeoutId = setTimeout(() => {
                          mutationObserver.disconnect();
                          clearInterval(processIntervalId);
                          resolveWithResolution(latestResolution);
                        }, waitForMinimumDuration + stableWaitDuration);
                      };

                      const mutationObserver = new MutationObserver(scheduleResolution);
                      mutationObserver.observe(container, {
                        subtree: true,
                        childList: true,
                        attributes: true,
                      });

                      const processIntervalId = setInterval(scheduleResolution, 250);
                      windowLoaded.then(scheduleResolution);
                      scheduleResolution();

                      // Fall back before the parent timeout if Instagram is unavailable.
                      setTimeout(() => {
                        mutationObserver.disconnect();
                        clearInterval(processIntervalId);
                        clearTimeout(resolveTimeoutId);
                        resolveWithResolution(latestResolution);
                      }, maximumWaitDuration);

                      return;
                    }

                    if (isGleamEmbed) {
                      let latestResolution = null;
                      let resolveTimeoutId = 0;
                      const minimumWaitDuration = 12000;
                      const stableWaitDuration = 2500;
                      const maximumWaitDuration = 18000;

                      const scheduleResolution = () => {
                        const iframe = container.querySelector(
                          'iframe[src*="gleam.io"], iframe[src*="gleamjs.io"], iframe'
                        );
                        if (iframe && !iframe.dataset.emakiHeightListener) {
                          iframe.dataset.emakiHeightListener = "true";
                          iframe.addEventListener("load", scheduleResolution);
                        }

                        const resolution = getGleamResolution();
                        if (!resolution) {
                          return;
                        }

                        latestResolution = resolution;
                        clearTimeout(resolveTimeoutId);
                        const elapsed = new Date().valueOf() - startTime.valueOf();
                        const waitForMinimumDuration = Math.max(
                          minimumWaitDuration - elapsed,
                          0
                        );
                        resolveTimeoutId = setTimeout(() => {
                          mutationObserver.disconnect();
                          clearInterval(processIntervalId);
                          resolveWithResolution(latestResolution);
                        }, waitForMinimumDuration + stableWaitDuration);
                      };

                      const mutationObserver = new MutationObserver(scheduleResolution);
                      mutationObserver.observe(container, {
                        subtree: true,
                        childList: true,
                        attributes: true,
                      });

                      const processIntervalId = setInterval(scheduleResolution, 250);
                      windowLoaded.then(scheduleResolution);
                      scheduleResolution();

                      // Fall back before the parent timeout if Gleam is unavailable.
                      setTimeout(() => {
                        mutationObserver.disconnect();
                        clearInterval(processIntervalId);
                        clearTimeout(resolveTimeoutId);
                        resolveWithResolution(latestResolution);
                      }, maximumWaitDuration);

                      return;
                    }

                    if (container.querySelector("iframe")) {
                      let latestResolution = null;
                      let resolveTimeoutId = 0;
                      const stableWaitDuration = 2500;
                      const maximumWaitDuration = 8000;

                      const scheduleResolution = () => {
                        const iframe = container.querySelector("iframe");
                        if (iframe && !iframe.dataset.emakiHeightListener) {
                          iframe.dataset.emakiHeightListener = "true";
                          iframe.addEventListener("load", scheduleResolution);
                        }

                        const resolution = getGenericIframeResolution();
                        if (!resolution) {
                          return;
                        }

                        if (
                          !latestResolution ||
                          resolution.height > latestResolution.height
                        ) {
                          latestResolution = resolution;
                        }
                        clearTimeout(resolveTimeoutId);
                        resolveTimeoutId = setTimeout(() => {
                          mutationObserver.disconnect();
                          clearInterval(processIntervalId);
                          resolveWithResolution(latestResolution);
                        }, stableWaitDuration);
                      };

                      const mutationObserver = new MutationObserver(scheduleResolution);
                      mutationObserver.observe(container, {
                        subtree: true,
                        childList: true,
                        attributes: true,
                      });

                      const processIntervalId = setInterval(scheduleResolution, 250);
                      windowLoaded.then(scheduleResolution);
                      scheduleResolution();

                      setTimeout(() => {
                        mutationObserver.disconnect();
                        clearInterval(processIntervalId);
                        clearTimeout(resolveTimeoutId);
                        resolveWithResolution(latestResolution);
                      }, maximumWaitDuration);

                      return;
                    }
                
                    // Allow up to 5 seconds to size the embed.
                    setTimeout(resolveWithResolution, 5000);
                
                    // If there is no JS script in the embed, resolve once window loaded.
                    // This allows us to obtain dimensions for an image/video that lacks
                    // width/height attributes.
                    const script = container.querySelector(
                      'script:not([type]), script[type="module"], script[type~="javascript"], *[onload]'
                    );
                    if (!script) {
                      const dimensionLessVideo = container.querySelector(
                        "video:not([width][height])"
                      );
                      if (dimensionLessVideo) {
                        if (dimensionLessVideo.videoWidth && dimensionLessVideo.videoHeight) {
                          resolveWithResolution();
                        } else {
                          dimensionLessVideo.addEventListener(
                            "loadedmetadata",
                            resolveWithResolution
                          );
                        }
                      } else {
                        windowLoaded.then(resolveWithResolution);
                      }
                      return;
                    }
              
                    // Start listening for DOM changes, and stop once a 2.5-second pause is encountered.
                    let resolveTimeoutId = 0;
                    const mutationObserver = new MutationObserver(() => {
                      clearTimeout(resolveTimeoutId);
                
                      const complete = () => {
                        mutationObserver.disconnect();
                        resolveWithResolution();
                      };
                
                      resolveTimeoutId = setTimeout(complete, 2500);
                    });
                    mutationObserver.observe(container, {
                      subtree: true,
                      childList: true,
                      attributes: true,
                    });
                  });
                }
              (async () => {
                const data = await watchForEmbedLoaded(
                  document.querySelector('div'),
                  ${isInstagramEmbed},
                  ${isGleamEmbed}
                );
                parent.postMessage(data);
              })();
              </script>
            </body>
            </html>
          `;

        if (signal.aborted) {
            return Promise.reject(new DOMException('Aborted', 'AbortError'));
        }

        return new Promise((resolve, reject) => {
            signal.addEventListener('abort', () => {
                // window.clearTimeout(reject);
                reject(new DOMException('Aborted', 'AbortError'));
            });
            window.addEventListener(
                "message",
                (event) => {
                    if (event.source === iframe.contentWindow) {
                        resolve(event.data);
                    }
                },
                {once: true}
            );
            iframe.contentWindow.document.open();
            iframe.contentWindow.document.write(html);
            iframe.contentWindow.document.close();

            setTimeout(reject, (isInstagramEmbed || isGleamEmbed) ? 20000 : 10000);
        });
    }

    abort(): void {

        this.controller.abort();
        const progress = document.querySelector("progress");
        progress.value = 0;

        const calculationSection = document.getElementById("calculation");
        calculationSection.hidden = true;
    }

}


// WEBPACK FOOTER //
// ./js/utils/embeds/EmbedsSizesGenerator.ts